<template>
    <div class="ticker-container" :class="{'minimized': isMinimized}">
        <div class="ticker-label-fixed">{{ label }}</div>
        <div class="ticker-wrapper">
            <div class="ticker-track" :style="{ animationDuration: animationDuration + 's' }">
                <div 
                    v-for="(item, index) in duplicatedItems" 
                    :key="index"
                    class="ticker-item"
                >
                    <span class="ticker-symbol">{{ item.symbol }}</span>
                    <span class="ticker-price">{{ formatPrice(item.price_inv) }}</span>
                    <span class="ticker-change" :class="getChangeClass(item.price_change_percent)">
                        {{ item.price_change_percent >= 0 ? '+' : '' }}{{ formatNumber(item.price_change_percent, 2) }}%
                    </span>
                </div>
            </div>
        </div>
        <div class="ticker-fade-overlay"></div>
    </div>
</template>

<script>
import { getTickersAll } from '@/api/portfolio'

export default {
    props: {
        label: {
            type: String,
            required: true
        },
        source: {
            type: String,
            required: true
        },
        animationDuration: {
            type: Number,
            default: 30
        }
    },
    data() {
        return {
            displayItems: [],
            queuedItems: [],
            offset: 0,
            batchSize: 50,
            isInitialized: false
        }
    },
    computed: {
        duplicatedItems() {
            // Double the items for seamless infinite loop
            // CSS animation moves -50%, then resets to 0 (looks identical)
            return [...this.displayItems, ...this.displayItems]
        }
    },
    async mounted() {
        await this.initialize()
    },
    beforeUnmount() {
        if (this.loadInterval) clearInterval(this.loadInterval)
        if (this.rotateInterval) clearInterval(this.rotateInterval)
    },
    methods: {
        async initialize() {
            await this.refreshTickers()
            this.isInitialized = true
            // Start background refresh every 60s
            this.loadInterval = setInterval(() => {
                this.refreshTickers()
            }, 60000)
            // Rotate items every 45s (when off-screen)
            this.rotateInterval = setInterval(() => {
                this.rotateItems()
            }, 45000)
        },
        async refreshTickers() {
            try {
                const result = await getTickersAll(200)
                const sourceData = result[this.source]
                if (sourceData && Array.isArray(sourceData.items) && sourceData.items.length > 0) {
                    this.queuedItems = sourceData.items.slice(0, 100)
                    this.displayItems = this.queuedItems.slice(0, 50)
                }
            } catch (error) {
                console.error('Failed to load ticker:', error)
            }
        },
        rotateItems() {
            // Smoothly rotate in new items from queue
            if (this.queuedItems.length > 50) {
                const newItems = this.queuedItems.slice(0, 50)
                this.queuedItems = this.queuedItems.slice(50)
                
                // Append new items (happens off-screen during scroll)
                this.displayItems = [...this.displayItems.slice(-50), ...newItems]
            }
        },
        formatPrice(value) {
            // For ticker: use scientific notation for extremely small prices
            if (value < 0.000001) {
                return value.toExponential(2)
            }
            
            return new Intl.NumberFormat('ro-RO', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 10,
                minimumSignificantDigits: 1,
                maximumSignificantDigits: 6
            }).format(value)
        },
        formatNumber(value, decimals) {
            return new Intl.NumberFormat('ro-RO', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            }).format(value)
        },
        getChangeClass(value) {
            if (value > 0) return 'positive'
            if (value < 0) return 'negative'
            return 'neutral'
        }
    }
}
</script>

<style scoped>
.ticker-container {
    overflow: hidden;
    height: 12px;
    position: relative;
    margin: 0;
    padding: 0;
    border: none;
}

.ticker-label-fixed {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 10;
    background: linear-gradient(to right, rgba(248, 249, 250, 1) 0%, rgba(248, 249, 250, 1) 70%, rgba(248, 249, 250, 0) 100%);
    padding: 0 1rem 0 0.5rem;
    display: flex;
    align-items: center;
    font-weight: 700;
    font-size: 0.75rem;
    color: #495057;
    min-width: 120px;
}

.dark .ticker-label-fixed {
    background: linear-gradient(to right, rgba(30, 41, 59, 1) 0%, rgba(30, 41, 59, 1) 70%, rgba(30, 41, 59, 0) 100%);
    color: #cbd5e1;
}

.ticker-wrapper {
    overflow: hidden;
    position: relative;
    height: 100%;
    padding-left: 120px;
}

.ticker-fade-overlay {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 100px;
    background: linear-gradient(to left, rgba(248, 249, 250, 1) 0%, rgba(248, 249, 250, 0) 100%);
    pointer-events: none;
    z-index: 10;
}

.dark .ticker-fade-overlay {
    background: linear-gradient(to left, rgba(30, 41, 59, 1) 0%, rgba(30, 41, 59, 0) 100%);
}

.ticker-track {
    display: flex;
    gap: 0.5rem;
    animation: scroll-ticker linear infinite;
    will-change: transform;
    align-items: center;
    height: 100%;
}

.ticker-track:hover {
    animation-play-state: paused;
}

@keyframes scroll-ticker {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(-50%);
    }
}

.ticker-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    white-space: nowrap;
    font-size: 0.75rem;
}

.ticker-symbol {
    font-weight: 700;
    color: #212529;
}

.dark .ticker-symbol {
    color: #f8fafc;
}

.ticker-price {
    color: #6c757d;
}

.dark .ticker-price {
    color: #94a3b8;
}

.ticker-change {
    font-weight: 600;
}

.ticker-change.positive {
    color: #22c55e;
}

.ticker-change.negative {
    color: #ef4444;
}

.ticker-change.neutral {
    color: #6c757d;
}

.dark .ticker-change.neutral {
    color: #94a3b8;
}
</style>
